import {View} from 'ol';
import {defaults as defaultControls} from 'ol/control.js';
import {Tile as TileLayer} from 'ol/layer';
import OSM from 'ol/source/OSM';
import {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import useDevices from '../../../hooks/use-devices';
import MapInstance from '../MapInstance';
import {actions, initialState, reducer} from '../reducers';
import useAlerts from 'src/hooks/use-alerts';
import {useIntl} from 'react-intl';

export const buildDeviceMap = (devices) => {
    const deviceMap = new Map();
    devices.forEach((d) => deviceMap.set(d.id, d));
    return deviceMap;
};

const useMap = (props = {}, ...plugins) => {
    const {tiers: tiles} = props;

    const intl = useIntl();
    const {toast} = useAlerts();
    const [selectedMap, setSelectedMap] = useState('osm');

    const instance = useRef({
        props: {
            center: [0, 0],
            zoom: 4,
            tiers: [],
            initAllDevices: false,
            cluster: {distance: 0},
            ...props,
        },
    });

    const {devices} = useDevices();

    const [state, dispatch] = useReducer(reducer, initialState);

    const {target, center, zoom} = instance.current.props;
    const tileNames = useMemo(() => ['osm', ...tiles.map((tile) => tile.get('name'))], [tiles]);

    const pluginActions = plugins.reduce(
        (actions, plugin) => ({
            ...actions,
            ...plugin(state, dispatch, instance),
        }),
        {},
    );

    const setMap = useCallback((mapName) => {
        setSelectedMap((prev) => {
            if (tileNames.includes(mapName)) {
                state.instance.setMap(mapName);
                return mapName;
            } else {
                toast.info(intl.formatMessage({id: 'mapKindNotAvailable'}, {mapName}));
                return prev;
            }
        });
    }, [tileNames, state.instance]);

    useEffect(() => {
        if (target && target.current) {
            const view = new View({center, zoom});

            const osm = new TileLayer({
                source: new OSM(),
                visible: false,
                name: 'osm',
            });

            const options = {
                view,
                layers: [osm, ...tiles],
                loadTilesWhileInteracting: true,
                loadTilesWhileAnimating: true,
                controls: defaultControls({
                    attribution: false,
                    zoom: false,
                    rotate: false,
                }),
                target: target.current,
            };

            const map = new MapInstance(options);
            map.setMap(selectedMap);

            dispatch({
                type: actions.SET_INSTANCE,
                payload: map,
            });

            instance.current = {
                ...instance.current,
                instance: map,
                pluginActions,
            };
        }
    }, [target]);

    useEffect(() => {
        if (state.instance && tiles.length > 0) {
            const existingLayers = state.instance.instance.getLayers().getArray();
            tiles.forEach((tile) => {
                const tileName = tile.get('name');
                const exists = existingLayers.some((layer) => layer.get('name') === tileName);
                if (!exists) {
                    state.instance.instance.addLayer(tile);
                }
            });
        }
    }, [state.instance, tiles]);

    // Reconstroi so quando o CONJUNTO de ids muda (dispositivo entrou ou saiu do usuario,
    // MON-570) -- nao a cada posicao. devices.list.length sozinho nao pegava troca com a
    // mesma contagem (3 saem, 3 entram), e o guard antigo (!state.deviceMap) travava a
    // reconstrucao pra sempre depois da primeira vez, deixando devicesMap.js e os hooks
    // que leem state.deviceMap (useMapLabel, useMapTrace/V2) presos no snapshot inicial.
    const deviceIdsKey = devices.devices.map((d) => d.id).join(',');

    useEffect(() => {
        if (!devices.list.length) return;

        dispatch({
            type: actions.SET_DEVICE_MAP,
            payload: buildDeviceMap([...devices.devices.values()]),
        });
    }, [deviceIdsKey]);

    return {
        ...instance.current,
        ...pluginActions,
        selectedMap,
        setSelectedMap: setMap,
        state,
    };
};

export default useMap;
